import {css, StyleSheet} from 'aphrodite';
import React, {useReducer} from 'react';

import {
  buildDefaultConsensusConfiguration,
  getSelectedSetups,
  isTemplateSetup,
  isValidSetup,
} from 'analyses/alignment/modals/SharedUtil';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {AlignmentPreferences, BulkAlignmentCreationKind} from 'constants/AlignmentConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import preferences from 'Preferences';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepItemStatus} from 'ui-library/StepNav';

import ChooseInputPane from './ChooseInputPane';
import reducer, {
  AlignmentInput,
  AlignmentSetup,
  CreateSequenceAlignmentModalAction,
  CreateSequenceAlignmentModalActionType,
  CreateSequenceAlignmentModalState,
  InputType,
  SequenceAnalysisInfo,
  StepKey,
} from './createSequenceAlignmentsModalReducer';
import DefineParametersPane from './DefineParametersPane';

function getInitialAlignmentKind(sequenceAnalysisInfo?: SequenceAnalysisInfo): BulkAlignmentCreationKind {
  if (sequenceAnalysisInfo && !isTemplateSetup(sequenceAnalysisInfo.alignmentSetup)) {
    return BulkAlignmentCreationKind.CONSENSUS;
  }
  return BulkAlignmentCreationKind.MULTISEQUENCE;
}

function getInitialState({
  initialInputs,
  initialState,
  realignmentInput,
}: Omit<CreateSequenceAlignmentsModalProps, 'onRequestClose'>): CreateSequenceAlignmentModalState {
  if (initialState) {
    return initialState;
  }

  const templateCandidates = initialInputs.filter(
    // In practice, only bioentity inputs should be passed as initialInput.
    (input) => input.type === InputType.BIOENTITY && input.canBeTemplate
  );
  // Only pre-set templates for Pairwise and Multisequence setups if there's exactly
  // one candidate.
  const initialTemplates = templateCandidates.length === 1 ? templateCandidates : [];

  const state = {
    statusByStep: {
      [StepKey.ChooseInput]: StepItemStatus.UNVISITED,
      [StepKey.DefineParameters]: StepItemStatus.UNVISITED,
    },
    currentStepKey: StepKey.ChooseInput,
    numFilesUploading: 0,
    inputs: initialInputs,
    folderId: FolderDefaults.get(FolderDefaultsKey.INVENTORY),
    program: preferences.get(AlignmentPreferences.ALIGNMENT_PROGRAM),
    mafftParameters: preferences.get(AlignmentPreferences.MAFFT_PARAMETERS),
    clustalOParameters: preferences.get(AlignmentPreferences.CLUSTAL_O_PARAMETERS),
    disableCircularTemplateRotation: BenchlingConfig.MOLBIO_ENABLE_CIRCULAR_ALIGNMENT_ROTATION_OPT_OUT
      ? preferences.get(AlignmentPreferences.DISABLE_CIRCULAR_TEMPLATE_ROTATION)
      : false,
    alignmentKind: getInitialAlignmentKind(realignmentInput?.sequenceAnalysisInfo),
    pairwiseConfiguration: realignmentInput
      ? []
      : initialTemplates.map((template) => ({
          template,
          items: [],
        })),
    multisequenceConfiguration:
      realignmentInput != null && isTemplateSetup(realignmentInput.sequenceAnalysisInfo.alignmentSetup)
        ? [realignmentInput.sequenceAnalysisInfo.alignmentSetup]
        : initialTemplates.map((template) => ({
            template,
            items: [],
          })),
    consensusConfiguration:
      realignmentInput && !isTemplateSetup(realignmentInput.sequenceAnalysisInfo.alignmentSetup)
        ? [realignmentInput.sequenceAnalysisInfo.alignmentSetup]
        : buildDefaultConsensusConfiguration(initialInputs),
    defineParametersPaneAccessed: false,
    realignmentInfo: realignmentInput,
    hasAutopopulatedTemplateGroup: {
      multisequenceConfiguration: false,
      pairwiseConfiguration: false,
    },
  };
  assert(
    state.realignmentInfo == null ||
      [BulkAlignmentCreationKind.MULTISEQUENCE, BulkAlignmentCreationKind.CONSENSUS].includes(
        state.alignmentKind
      ),
    'Pairwise alignments are not allowed for realignments.'
  );
  assert(
    state.realignmentInfo == null ||
      state.alignmentKind !== BulkAlignmentCreationKind.MULTISEQUENCE ||
      state.multisequenceConfiguration.length === 1,
    'Realignments may only be performed for a single template at a time.'
  );
  return state;
}

export interface CreateSequenceAlignmentsModalProps {
  onRequestClose: () => void;
  initialInputs: Array<AlignmentInput>;
  initialState?: CreateSequenceAlignmentModalState;
  realignmentInput?: {
    sequenceAnalysisInfo: SequenceAnalysisInfo;
    isTemplateSequenceEditable: boolean;
  };
}

function getStepModalButtons(
  step: StepKey,
  uploadInProgress: boolean,
  hasMinimalInput: boolean,
  dispatch: React.Dispatch<CreateSequenceAlignmentModalAction>,
  onRequestClose: () => void,
  folderId: CreateSequenceAlignmentModalState['folderId'],
  alignmentKind: CreateSequenceAlignmentModalState['alignmentKind'],
  pairwiseConfiguration: CreateSequenceAlignmentModalState['pairwiseConfiguration'],
  multisequenceConfiguration: CreateSequenceAlignmentModalState['multisequenceConfiguration'],
  consensusConfiguration: CreateSequenceAlignmentModalState['consensusConfiguration'],
  isRealign: boolean
): Array<ModalButton> {
  const alignmentSetups = getSelectedSetups(
    alignmentKind,
    pairwiseConfiguration,
    multisequenceConfiguration,
    consensusConfiguration
  );

  // Disable the button if there are no setups, or if any of the setups are invalid.
  const createAlignmentButtonDisabled =
    alignmentSetups.length === 0 ||
    alignmentSetups.some((setup: AlignmentSetup) => !isValidSetup(setup, Boolean(folderId), isRealign));

  if (step === StepKey.ChooseInput) {
    return [
      {text: 'Cancel', cancel: true},
      {
        text: 'Next',
        submit: true,
        style: ButtonStyle.SECONDARY,
        onClick: () => {
          dispatch({
            type: CreateSequenceAlignmentModalActionType.UPDATE_CURRENT_STEP,
            value: StepKey.DefineParameters,
          });
        },
        disabled: uploadInProgress || !hasMinimalInput,
        dataTestAttrs: {component: 'CreateSequenceAlignmentsModal', key: ModalButtonKey.NEXT},
      },
    ];
  } else {
    return [
      {
        text: 'Back',
        style: ButtonStyle.TERTIARY_GRAY,
        onClick: () => {
          dispatch({
            type: CreateSequenceAlignmentModalActionType.UPDATE_CURRENT_STEP,
            value: StepKey.ChooseInput,
          });
        },
      },
      {
        text: isRealign ? 'Realign' : 'Create Alignment',
        submit: true,
        style: ButtonStyle.PRIMARY,
        onClick: () => {
          dispatch({
            type: CreateSequenceAlignmentModalActionType.PERFORM_ALIGNMENTS,
          });
          onRequestClose();
        },
        disabled: createAlignmentButtonDisabled,
      },
    ];
  }
}

export default function CreateSequenceAlignmentsModal({
  onRequestClose,
  initialInputs,
  initialState,
  realignmentInput,
}: CreateSequenceAlignmentsModalProps): JSX.Element {
  const [
    {
      program,
      alignmentKind,
      currentStepKey,
      inputs,
      folderId,
      mafftParameters,
      clustalOParameters,
      disableCircularTemplateRotation,
      numFilesUploading,
      statusByStep,
      pairwiseConfiguration,
      multisequenceConfiguration,
      consensusConfiguration,
      realignmentInfo,
    },
    dispatch,
  ] = useReducer(reducer, {initialInputs, initialState, realignmentInput}, getInitialState);
  const isRealign = Boolean(realignmentInfo);

  let numInputs = inputs.length;
  if (realignmentInfo) {
    if (isTemplateSetup(realignmentInfo.sequenceAnalysisInfo.alignmentSetup)) {
      numInputs += 1;
    }
    numInputs += realignmentInfo.sequenceAnalysisInfo.alignmentSetup.items.length;
  }

  const buttons = getStepModalButtons(
    currentStepKey,
    Boolean(numFilesUploading),
    numInputs > 1,
    dispatch,
    onRequestClose,
    folderId,
    alignmentKind,
    pairwiseConfiguration,
    multisequenceConfiguration,
    consensusConfiguration,
    isRealign
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      title={
        isRealign
          ? alignmentKind === BulkAlignmentCreationKind.CONSENSUS
            ? `Realign ${consensusConfiguration[0].nucleotideType}` // on a realign, there should only be one configuration
            : 'Realign DNA / RNA'
          : 'Create DNA / RNA alignment'
      }
      large={true}
      buttons={buttons}
      dataTestAttrs={{component: 'CreateSequenceAlignmentsModal', element: 'root'}}
      analyticsModule={ANALYTICS_MODULE.ANALYSIS_TOOL}
      analyticsModalType="create_sequence_alignments"
      analyticsMetadata={{isRealign}}
    >
      <div className={css(styles.stepNavMarginAdjustment)}>
        <StepNav<StepKey>
          steps={[
            {
              key: StepKey.ChooseInput,
              title: 'Choose input',
            },
            {
              key: StepKey.DefineParameters,
              title: 'Define parameters',
            },
          ]}
          statusByStep={statusByStep}
          currentStepKey={currentStepKey}
          onUpdateCurrentStep={(newStepKey: StepKey): void => {
            dispatch({type: CreateSequenceAlignmentModalActionType.UPDATE_CURRENT_STEP, value: newStepKey});
          }}
          disabled={numFilesUploading > 0}
        />
      </div>
      <>
        {currentStepKey === StepKey.ChooseInput && (
          <ChooseInputPane
            dispatch={dispatch}
            inputs={inputs}
            sequenceAnalysisInfo={realignmentInfo?.sequenceAnalysisInfo}
          />
        )}
        {currentStepKey === StepKey.DefineParameters && (
          <DefineParametersPane
            dispatch={dispatch}
            program={program}
            alignmentKind={alignmentKind}
            mafftParameters={mafftParameters}
            clustalOParameters={clustalOParameters}
            pairwiseConfiguration={pairwiseConfiguration}
            multisequenceConfiguration={multisequenceConfiguration}
            consensusConfiguration={consensusConfiguration}
            inputs={inputs}
            folderId={folderId}
            disableCircularTemplateRotation={disableCircularTemplateRotation}
            realignmentInfo={realignmentInfo}
          />
        )}
      </>
    </Modal>
  );
}

const styles = StyleSheet.create({
  stepNavMarginAdjustment: {marginBottom: '13px'},
});
